import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import {
  ActionButtons,
  IconButton,
  SummaryGrid,
} from "./personSummaryRow.styled";

export interface PersonSummaryLabels {
  readonly absence: string;
  readonly approvedTime: string;
  readonly collapse: string;
  readonly deviation: string;
  readonly download: string;
  readonly expand: string;
  readonly plannedTime: string;
  readonly shifts: string;
  readonly toApprove: string;
  readonly unreported: string;
}

export interface PersonSummaryMetrics {
  readonly absenceTime: string;
  readonly approvedTime: string;
  readonly deviationTime: string;
  readonly plannedTime: string;
  readonly shifts: number;
  readonly toApproveTime: string;
  readonly unreportedTime: string;
}

interface PersonSummaryDetailsProps {
  readonly avatarUrl: string | null;
  readonly isDetailed: boolean;
  readonly isExpanded: boolean;
  readonly labels: PersonSummaryLabels;
  readonly name: string;
  readonly role: string | null;
  readonly showDeviation: boolean;
  readonly summary: PersonSummaryMetrics;
  readonly teamLabel: string;
  readonly isZeroLikeValue: (value: string) => boolean;
  readonly onDownloadClick: () => void;
  readonly onToggleExpand: () => void;
}

export const PersonSummaryDetails = (
  props: PersonSummaryDetailsProps,
): JSX.Element => {
  const tags = [props.teamLabel];
  const metrics: Array<{
    id: string;
    label: string;
    value: string;
    muted?: boolean;
  }> = [
    {
      id: "shifts",
      label: props.labels.shifts,
      value: String(props.summary.shifts),
      muted: false,
    },
    {
      id: "planned-time",
      label: props.labels.plannedTime,
      value: props.summary.plannedTime,
      muted: props.isZeroLikeValue(props.summary.plannedTime),
    },
    {
      id: "approved-time",
      label: props.labels.approvedTime,
      value: props.summary.approvedTime,
      muted: props.isZeroLikeValue(props.summary.approvedTime),
    },
    {
      id: "to-approve-time",
      label: props.labels.toApprove,
      value: props.summary.toApproveTime,
      muted: props.isZeroLikeValue(props.summary.toApproveTime),
    },
    {
      id: "unreported-time",
      label: props.labels.unreported,
      value: props.summary.unreportedTime,
      muted: props.isZeroLikeValue(props.summary.unreportedTime),
    },
    {
      id: "absence-time",
      label: props.labels.absence,
      value: props.summary.absenceTime,
      muted: props.isZeroLikeValue(props.summary.absenceTime),
    },
  ];

  if (props.showDeviation) {
    metrics.push({
      id: "deviation-time",
      label: props.labels.deviation,
      value: props.summary.deviationTime,
      muted: props.isZeroLikeValue(props.summary.deviationTime),
    });
  }

  return (
    <SummaryGrid
      $metricColumns={metrics.length}
      $isDetailed={props.isDetailed}
      $hasDeviation={props.showDeviation}
    >
      <AvatarChip
        title={props.name}
        subTitle={props.role ?? ""}
        tags={tags}
        avatarUrl={props.avatarUrl}
      />
      {metrics.map((metric) => (
        <LabeledValue
          key={metric.id}
          value={metric.value}
          label={metric.label}
          muted={metric.muted}
        />
      ))}
      <ActionButtons>
        <IconButton
          type="button"
          aria-label={props.labels.download}
          onClick={props.onDownloadClick}
        >
          <CustomIcon name="v2/download" size="20px" color={ColorV2.Blue1} />
        </IconButton>
        <IconButton
          type="button"
          aria-label={
            props.isExpanded ? props.labels.collapse : props.labels.expand
          }
          aria-expanded={props.isExpanded}
          onClick={props.onToggleExpand}
        >
          <CustomIcon
            name={props.isExpanded ? "v2/up-chevron" : "v2/down-chevron"}
            size="20px"
            color={ColorV2.Blue1}
          />
        </IconButton>
      </ActionButtons>
    </SummaryGrid>
  );
};
